/**
 * NOTE: react-popper-tooltip doesn't interact well with Jest, so is mocked out.
 */
import { Spacing } from '@persona-id/design-tokens';
import { ComponentProps, ReactNode } from 'react';
import TooltipTrigger from 'react-popper-tooltip';
import styled from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import { Elevation } from 'design-system/variables/effect';

import 'react-popper-tooltip/dist/styles.css';

const Popover = styled('div')<{ noPointerEvents: boolean }>`
  // Adding !important to overwrite vendor styles
  box-shadow: ${Elevation.High} !important;
  border: 0 !important;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  padding: ${Spacing[2]};
  background-color: ${({ theme }) => theme.color.background.primary};
  z-index: ${Z_INDICES.toast} !important;
  pointer-events: ${({ noPointerEvents }) => (noPointerEvents ? 'none' : 'unset')};
`;

type ReactPopperTooltipProps = ComponentProps<typeof TooltipTrigger>;

interface TooltipProps
  extends Pick<
    Partial<ReactPopperTooltipProps>,
    Exclude<keyof ReactPopperTooltipProps, 'children' | 'tooltip'>
  > {
  children: ReactNode;
  tooltip: ReactNode;
  hideArrow?: boolean;
  className?: string;
  disabled?: boolean;
  enablePopoverClickEvents?: boolean;
}

// NB: mostly copied from https://github.com/mohsinulhaq/react-popper-tooltip#quick-start
const Tooltip = ({
  children,
  tooltip,
  hideArrow,
  className,
  disabled,
  trigger,
  enablePopoverClickEvents,
  ...props
}: TooltipProps) =>
  disabled || !tooltip ? (
    <>{children}</>
  ) : (
    <TooltipTrigger
      trigger={trigger}
      {...props}
      tooltip={({ arrowRef, tooltipRef, getArrowProps, getTooltipProps, placement }) => (
        <Popover
          id='dashboard__popover'
          className={`tooltip-container ${className ?? ''}`}
          noPointerEvents={trigger === 'hover'}
          {...getTooltipProps({
            ref: tooltipRef,
          })}
          onClick={(e) => {
            if (!enablePopoverClickEvents) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          {!hideArrow && (
            <div
              className='tooltip-arrow'
              {...getArrowProps({
                ref: arrowRef,
                'data-placement': placement,
              })}
            />
          )}
          {tooltip}
        </Popover>
      )}
    >
      {({ getTriggerProps, triggerRef }) => (
        <span
          {...getTriggerProps({
            ref: triggerRef,
            className: 'trigger',
          })}
        >
          {children}
        </span>
      )}
    </TooltipTrigger>
  );

export default Tooltip;
